import {useEffect, useRef} from "react";

import {LivePlayerController, RemotePlayerController} from "@repo/shared";
import {
  Unsubscribe,
  get,
  onChildAdded,
  ref,
  remove,
  set,
} from "firebase/database";

import {realtimeDB} from "~/firebase/firebase";

interface ActionSyncParams {
  participants: Record<string, true> | null;
  roundId: string | null;
  localPlayerId: string | null;
  liveController: LivePlayerController | null;
  remoteControllers: RemotePlayerController[];
}

/**
 * Synchronizes player actions using an append-only set of flip indices:
 *
 * actions/
 *   playerId/
 *     roundId/
 *       flipIndices/
 *         15: true
 *         27: true
 *         42: true
 */
export const usePlayerActionSync = ({
  participants,
  roundId,
  localPlayerId,
  liveController,
  remoteControllers,
}: ActionSyncParams) => {
  const maxSyncedFlip = useRef(-1);

  // ---------------------------------------------------------------------------
  // SEND LOCAL ACTIONS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!roundId || !localPlayerId || !liveController) {
      return;
    }

    const playerRoundRef = ref(
      realtimeDB,
      `actions/${localPlayerId}/${roundId}`,
    );

    maxSyncedFlip.current = -1;

    // Defensive cleanup in case we somehow re-enter the same round.
    remove(playerRoundRef).catch((err) =>
      console.error("Initial round cleanup failed:", err),
    );

    const syncLocalActions = () => {
      const currentActions = liveController.recordedActions;

      if (currentActions.length === 0) {
        return;
      }

      const newestFlipIndex = currentActions.length - 1;

      if (newestFlipIndex <= maxSyncedFlip.current) {
        return;
      }

      set(
        ref(
          realtimeDB,
          `actions/${localPlayerId}/${roundId}/flipIndices/${newestFlipIndex}`,
        ),
        true,
      ).catch((err) => console.error("Action sync write failed:", err));

      maxSyncedFlip.current = newestFlipIndex;
    };

    liveController.on("actions-changed", syncLocalActions);

    return () => {
      liveController.off("actions-changed", syncLocalActions);

      remove(playerRoundRef).catch((err) =>
        console.error("Round cleanup failed:", err),
      );
    };
  }, [roundId, localPlayerId, liveController]);

  // ---------------------------------------------------------------------------
  // RECEIVE REMOTE ACTIONS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!roundId || !participants || remoteControllers.length === 0) {
      return;
    }

    const subscriptions: Unsubscribe[] = [];

    const controllerMap = new Map(remoteControllers.map((c) => [c.ownerId, c]));

    for (const playerId in participants) {
      if (playerId === localPlayerId) {
        continue;
      }

      const remoteController = controllerMap.get(playerId);

      if (!remoteController) {
        continue;
      }

      const playerIndicesRef = ref(
        realtimeDB,
        `actions/${playerId}/${roundId}/flipIndices`,
      );

      let localIndices: number[] = [];

      const updateController = () => {
        remoteController.onRemoteActionsReceived(
          constructFlippedArray(localIndices),
        );
      };

      get(playerIndicesRef)
        .then((snapshot) => {
          if (!snapshot.exists()) {
            localIndices = [];
            updateController();
            return;
          }

          localIndices = Object.keys(snapshot.val())
            .map(Number)
            .filter(Number.isFinite);

          updateController();
        })
        .catch((err) =>
          console.error(`Initial fetch for player ${playerId} failed:`, err),
        );

      const unsubscribe = onChildAdded(playerIndicesRef, (snapshot) => {
        const key = snapshot.key;

        if (!key) {
          return;
        }

        const flipIndex = Number(key);

        if (!Number.isFinite(flipIndex)) {
          return;
        }

        localIndices.push(flipIndex);

        updateController();
      });

      subscriptions.push(unsubscribe);
    }

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [roundId, participants, localPlayerId, remoteControllers]);
};

/**
 * Constructs a boolean array by flipping state at the provided indices.
 */
function constructFlippedArray(flipIndices: number[]): boolean[] {
  if (flipIndices.length === 0) {
    return [];
  }

  const size = flipIndices[flipIndices.length - 1]! + 1;

  const result = new Array<boolean>(size);

  let currentValue = false;
  let flipPointer = 0;

  for (let i = 0; i < size; i++) {
    if (flipPointer < flipIndices.length && i === flipIndices[flipPointer]) {
      currentValue = !currentValue;
      flipPointer++;
    }

    result[i] = currentValue;
  }

  return result;
}
